/**
 * Mis alimentos (Req. 19.7, 20.1, 20.2, 20.5, 20.6) y favoritos de la base.
 *
 * Los alimentos propios salen de la etiqueta: por 100 g o por porción, con
 * porciones adicionales. Editarlos o borrarlos no cambia las comidas ya
 * registradas, que llevan su copia de valores (Req. 18.4).
 */

import { db, asegurarSettings } from './schema';
import { enEscritura } from './tx';
import type { Food, Porcion } from './types';
import { ahoraIso } from '@/domain/dates';
import { GRAMOS, LIMITE_GRAMOS, LIMITE_KCAL, type AlimentoBase } from '@/domain/nutricion';
import { normalizar } from '@/domain/busqueda';
import { ErrorValidacion } from '@/domain/errors';

/** LECTURA PURA: todos, archivados incluidos, por nombre. */
export async function leerMisAlimentos(): Promise<Food[]> {
  const alimentos = await db.foods.toArray();
  return alimentos.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

export async function leerAlimento(id: string): Promise<Food | undefined> {
  return db.foods.get(id);
}

const valorValido = (v: number | null) => v === null || (Number.isFinite(v) && v >= 0 && v <= LIMITE_KCAL);

function validarPorcion(p: Porcion, que: string): void {
  if (p.nombre.trim() === '') throw new ErrorValidacion(`Escribe el nombre de ${que}.`);
  if (normalizar(p.nombre) === GRAMOS) {
    throw new ErrorValidacion('"g" ya está disponible siempre que haya gramos: usa otro nombre para la porción.');
  }
  if (p.gramos !== null && !(Number.isFinite(p.gramos) && p.gramos > 0 && p.gramos <= LIMITE_GRAMOS)) {
    throw new ErrorValidacion(`Los gramos de ${que} deben ser mayores que cero y hasta 5,000.`);
  }
}

/** La nota de un alimento: fuente, micronutrientes de la etiqueta o cómo se calculó (0.3.5). */
export const NOTA_ALIMENTO_MAXIMA = 800;

export function validarAlimento(f: Food): void {
  if (f.nombre.trim() === '') throw new ErrorValidacion('Escribe el nombre del alimento.', 'nombre');
  if (f.nombre.trim().length > 80) throw new ErrorValidacion('El nombre es demasiado largo.', 'nombre');
  if (typeof f.nota === 'string' && f.nota.trim().length > NOTA_ALIMENTO_MAXIMA) {
    throw new ErrorValidacion(`La nota puede tener hasta ${NOTA_ALIMENTO_MAXIMA} caracteres.`, 'nota');
  }
  if (f.referencia.tipo === 'porPorcion') validarPorcion(f.referencia.porcion, 'la porción de la etiqueta');
  const { kcal, proteinaG, carbohidratosG, grasaG, fibraG } = f.nutrientes;
  if (!(Number.isFinite(kcal) && kcal >= 0 && kcal <= LIMITE_KCAL)) {
    throw new ErrorValidacion('Las kcal deben estar entre 0 y 10,000.', 'kcal');
  }
  if (![proteinaG, carbohidratosG, grasaG, fibraG].every(valorValido)) {
    throw new ErrorValidacion('Los gramos de los macros no pueden ser negativos.');
  }
  const nombres = new Set<string>();
  if (f.referencia.tipo === 'porPorcion') nombres.add(normalizar(f.referencia.porcion.nombre));
  for (const p of f.porciones) {
    validarPorcion(p, 'cada porción');
    const clave = normalizar(p.nombre);
    if (nombres.has(clave)) throw new ErrorValidacion(`La porción "${p.nombre}" está repetida.`);
    nombres.add(clave);
  }
}

/** Guarda nuevo o editado, con put por id (Req. 1.9). ESCRIBE. */
export async function guardarAlimento(alimento: Food): Promise<Food> {
  const nota = typeof alimento.nota === 'string' ? alimento.nota.trim() : null;
  const limpio: Food = {
    ...alimento,
    ...(alimento.nota === undefined ? {} : { nota: nota === '' ? null : nota }),
    nombre: alimento.nombre.trim(),
    porciones: alimento.porciones.map((p) => ({ nombre: p.nombre.trim(), gramos: p.gramos })),
    referencia:
      alimento.referencia.tipo === 'porPorcion'
        ? {
            tipo: 'porPorcion',
            porcion: { nombre: alimento.referencia.porcion.nombre.trim(), gramos: alimento.referencia.porcion.gramos },
          }
        : alimento.referencia,
    actualizadoEn: ahoraIso(),
  };
  validarAlimento(limpio);
  await enEscritura([db.foods], async () => {
    await db.foods.put(limpio);
  });
  return limpio;
}

async function cambiar(id: string, cambios: (f: Food) => Partial<Food>): Promise<void> {
  await enEscritura([db.foods], async () => {
    const alimento = await db.foods.get(id);
    if (alimento === undefined) throw new ErrorValidacion('Ese alimento ya no existe.');
    await db.foods.put({ ...alimento, ...cambios(alimento), actualizadoEn: ahoraIso() });
  });
}

/** Archivado = no aparece al buscar; lo registrado no cambia. */
export async function archivarAlimento(id: string, archivado: boolean): Promise<void> {
  await cambiar(id, () => ({ archivado }));
}

export async function alternarFavorito(id: string): Promise<void> {
  await cambiar(id, (f) => ({ favorito: !f.favorito }));
}

/** ¿Lo usa algún platillo? Entonces solo se puede archivar (Req. 20.5). */
export async function usadoEnPlatillos(id: string): Promise<number> {
  return db.dishes.filter((d) => d.ingredientes.some((i) => i.origen === 'propio' && i.alimentoId === id)).count();
}

export async function borrarAlimento(id: string): Promise<void> {
  await enEscritura([db.foods, db.dishes, db.settings], async () => {
    const enUso = await db.dishes
      .filter((d) => d.ingredientes.some((i) => i.origen === 'propio' && i.alimentoId === id))
      .count();
    if (enUso > 0) {
      throw new ErrorValidacion('Este alimento está en un platillo: archívalo en lugar de borrarlo.');
    }
    // Las comidas habituales (0.3.5) también lo necesitan para calcularse
    const habituales = (await db.settings.get(1))?.comidasHabituales;
    if (Array.isArray(habituales) && habituales.some((h) => h.items.some((i) => i.origen === 'propio' && i.alimentoId === id))) {
      throw new ErrorValidacion('Este alimento está en una comida habitual: archívalo en lugar de borrarlo.');
    }
    await db.foods.delete(id);
  });
}

/** Marca o desmarca un alimento de la base como favorito (en los ajustes). */
export async function alternarFavoritoBase(baseId: string): Promise<void> {
  await asegurarSettings();
  await enEscritura([db.settings], async () => {
    const ajustes = await db.settings.get(1);
    if (ajustes === undefined) return;
    const actuales = Array.isArray(ajustes.favoritosBase) ? ajustes.favoritosBase : [];
    const favoritosBase = actuales.includes(baseId)
      ? actuales.filter((id) => id !== baseId)
      : [...actuales, baseId];
    await db.settings.update(1, { favoritosBase });
  });
}

/**
 * Un alimento propio con los valores de uno de la base, para ajustarlo antes
 * de guardarlo (Req. 19.7). No se guarda aquí.
 */
export function borradorDesdeBase(base: AlimentoBase, id: string): Food {
  const ahora = ahoraIso();
  return {
    id,
    nombre: base.nombre,
    referencia: { tipo: 'por100g' },
    nutrientes: { ...base.por100g },
    porciones: base.porciones.map((p) => ({ ...p })),
    rompeAyuno: base.rompeAyuno,
    favorito: false,
    archivado: false,
    origenBaseId: base.id,
    estado: base.estado,
    creadoEn: ahora,
    actualizadoEn: ahora,
  };
}
